import { useState } from "react";
import { isObjectEmpty } from "../utils";
import { MULTI_SELECT_INPUT_TYPES } from "../constants";
import { QuestionType } from "@fhc/shared/constants";

/**
 * Custom hook for storing survey responses.
 *
 * @param {object} args
 * @param {number} args.questionIndex The current step in the survey.
 * @param {object[] | undefined} args.questions The questions for the survey.
 *
 * @returns {{
 *  selections: object[];
 *  selectChoice: (choice, id) => void;
 * }} Survey state objects and functions for managing the state.
 *  - `selections` is an array that has responses to the questions
 *  - `selectChoice` handles changing state when a question is responded to
 *
 */
function useSurvey({ questionIndex, questions }) {
    const [selections, setSelections] = useState({});

    function selectChoice(choice, questionId) {
        const questionType = questions[questionIndex].type;

        switch (questionType) {
            case QuestionType.RATING_SCALE:
            case QuestionType.TEXTBOX:
                handleRatingOrTextbox(choice, questionId);
                break;

            case QuestionType.SINGLE_RESPONSE:
                handleSingleChoice(choice, questionId);
                break;

            case QuestionType.MULTIPLE_RESPONSE:
                handleMultipleChoice(choice, questionId);
                break;

            case QuestionType.MATRIX:
                handleTableSheet(choice, questionId);
                break;

            default:
                throw new Error(`"${questionType}" is an invalid question type`);
        }
    }

    //#region Functions for handling each question type
    function handleRatingOrTextbox(choice, questionId) {
        setSelections((prevSelections) => ({
            ...prevSelections,
            [questionId]: choice,
        }));
    }

    function handleSingleChoice(choice, questionId) {
        if (choice.id) {
            setSelections((prevSelections) => ({
                ...prevSelections,
                [questionId]: { [choice.id]: { ...choice } },
            }));
        } else {
            setSelections((prevSelections) => {
                const updatedSelections = { ...prevSelections };
                delete updatedSelections[questionId];
                return updatedSelections;
            });
        }
    }

    function handleMultipleChoice(choice, questionId) {
        // Unselect if the option has already been selected
        if (selections[questionId] && selections[questionId][choice.id]) {
            const newSelections = { ...selections };
            delete newSelections[questionId][choice.id];

            if (isObjectEmpty(newSelections[questionId])) {
                delete newSelections[questionId];
            }

            setSelections(newSelections);
        } else {
            // Create a copy of the selections object and add the answer to it
            let newSelections = {
                ...selections,
                [questionId]: { ...selections[questionId], [choice.id]: choice },
            };

            newSelections = unselectMultipleChoiceInputs(newSelections, questionId, choice);

            setSelections(newSelections);
        }
    }

    function handleTableSheet(choice, questionId) {
        const newSelections = { ...selections };
        const currentChoice = selections[questionId]?.[choice.id];

        // Unselect the choice if it is already selected
        if (currentChoice?.id === choice.id && currentChoice?.answer === choice.answer) {
            delete newSelections[questionId][choice.id];
            if (isObjectEmpty(newSelections[questionId])) {
                delete newSelections[questionId];
            }
        } else {
            // Add the new choice or update it if there's already a choice selected in the same row
            newSelections[questionId] = {
                ...(newSelections[questionId] || {}),
                [choice.id]: { ...choice },
            };
        }

        setSelections(newSelections);
    }
    //#endregion

    //#region Helper function(s)
    function unselectMultipleChoiceInputs(selections, questionId, choice) {
        const newSelections = { ...selections };

        Object.keys(newSelections[questionId]).forEach((key) => {
            const currentSelection = newSelections[questionId][key];

            if (
                currentSelection.inputType !== choice.inputType ||
                (choice.inputType === MULTI_SELECT_INPUT_TYPES.radio && choice.id !== currentSelection.id)
            ) {
                delete newSelections[questionId][key];
            }
        });

        return newSelections;
    }
    //#endregion

    return { selections, selectChoice };
}

export default useSurvey;
